import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Fab,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Stack,
  CardActions,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useApi, useAsyncOperation } from '../../hooks/useApi';
import { apiService } from '../../services/api';
import { Client } from '../../types';
import { formatDate, getStatusColor, getStatusLabel } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';

interface ClientRowProps {
  client: Client;
  onEdit: (client: Client) => void;
  onView: (client: Client) => void;
  onDeactivate: (client: Client) => void;
}

const ClientRow: React.FC<ClientRowProps> = ({ client, onEdit, onView, onDeactivate }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <TableRow hover>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {client.type === 'business' ? <BusinessIcon /> : getInitials(client.name)}
          </Avatar>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {client.name}
            </Typography>
            {client.businessName && (
              <Typography variant="body2" color="text.secondary">
                {client.businessName}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{client.email}</Typography>
        <Typography variant="body2" color="text.secondary">
          {client.phoneNumber}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={client.type === 'individual' ? 'Individual' : 'Empresa'}
          color={client.type === 'individual' ? 'primary' : 'secondary'}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Chip
          label={getStatusLabel(client.status)}
          sx={{
            backgroundColor: getStatusColor(client.status) + '20',
            color: getStatusColor(client.status),
            border: `1px solid ${getStatusColor(client.status)}`,
          }}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {formatDate(client.createdAt)}
        </Typography>
      </TableCell>
      <TableCell>
        <IconButton onClick={handleMenuOpen} size="small">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => { onView(client); handleMenuClose(); }}>
            <ViewIcon sx={{ mr: 1 }} fontSize="small" />
            Ver Detalles
          </MenuItem>
          <MenuItem onClick={() => { onEdit(client); handleMenuClose(); }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Editar
          </MenuItem>
          {client.status === 'active' && (
            <MenuItem onClick={() => { onDeactivate(client); handleMenuClose(); }}>
              <BlockIcon sx={{ mr: 1 }} fontSize="small" />
              Desactivar
            </MenuItem>
          )}
        </Menu>
      </TableCell>
    </TableRow>
  );
};

export const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    action: () => {},
  });

  const {
    data: clientsData,
    loading,
    error,
    execute: fetchClients,
  } = useApi<{ clients: Client[]; total: number; globalTotal: number; pages: number }>(
    () => apiService.getClients(page + 1, rowsPerPage, searchTerm || undefined),
    { immediate: false }
  );

  const { execute: deactivateClient, loading: deactivating } = useAsyncOperation();

  const {
    data: clientStats,
    loading: statsLoading,
    execute: fetchStats,
  } = useApi<any>(() => apiService.getClientStats(), { immediate: false });

  useEffect(() => {
    fetchClients();
    fetchStats();
  }, [page, rowsPerPage, searchTerm, fetchClients, fetchStats]);




  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (client: Client) => {
    navigate(`/clients/${client._id}`);
  };

  const handleEdit = (client: Client) => {
    navigate(`/clients/${client._id}/edit`);
  };

  const handleDeactivate = (client: Client) => {
    setConfirmDialog({
      open: true,
      title: 'Confirmar Desactivación',
      message: `¿Está seguro que desea desactivar al cliente ${client.name}? Esta acción se puede revertir más tarde.`,
      action: async () => {
        const result = await deactivateClient(() => apiService.deactivateClient(client._id));
        if (result) {
          fetchClients();
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
    });
  };

  const handleAddClient = () => {
    navigate('/clients/new');
  };

  if (error) {
    return (
      <Alert severity="error">
        Error cargando clientes: {error}
        <Button onClick={() => fetchClients()} sx={{ ml: 2 }}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  // Componente de tarjeta para vista móvil
  const ClientCard: React.FC<{ client: Client }> = ({ client }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const getInitials = (name: string) => {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {client.type === 'business' ? <BusinessIcon /> : getInitials(client.name)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }} noWrap>
                  {client.name}
                </Typography>
                {client.businessName && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {client.businessName}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { handleView(client); setAnchorEl(null); }}>
                <ViewIcon sx={{ mr: 1 }} fontSize="small" /> Ver Detalles
              </MenuItem>
              <MenuItem onClick={() => { handleEdit(client); setAnchorEl(null); }}>
                <EditIcon sx={{ mr: 1 }} fontSize="small" /> Editar
              </MenuItem>
              {client.status === 'active' && (
                <MenuItem onClick={() => { handleDeactivate(client); setAnchorEl(null); }}>
                  <BlockIcon sx={{ mr: 1 }} fontSize="small" /> Desactivar
                </MenuItem>
              )}
            </Menu>
          </Box>
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2" noWrap>{client.email}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2">{client.phoneNumber}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Chip
                label={client.type === 'individual' ? 'Individual' : 'Empresa'}
                color={client.type === 'individual' ? 'primary' : 'secondary'}
                size="small"
              />
              <Chip
                label={getStatusLabel(client.status)}
                sx={{
                  backgroundColor: getStatusColor(client.status) + '20',
                  color: getStatusColor(client.status),
                  border: `1px solid ${getStatusColor(client.status)}`,
                }}
                size="small"
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: { xs: 2, md: 4 }
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            Gestión de Clientes
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Administra todos los clientes del sistema
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClient}
          size={isMobile ? 'medium' : 'large'}
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          Nuevo Cliente
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                {clientStats?.data?.summary?.total || 0}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Total Clientes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                {clientStats?.data?.summary?.active || 0}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Activos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                {clientStats?.data?.summary?.dealers || 0}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Dealers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main" sx={{ fontWeight: 600 }}>
                {clientStats?.data?.summary?.individuals || 0}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Individuales
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Buscar clientes por nombre, email o teléfono..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
        </Box>
      </Paper>

      {/* Clients Table / Cards */}
      {isMobile ? (
        // Vista móvil con cards
        <Box>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !(clientsData as any)?.clients || (clientsData as any).clients?.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchTerm ? 'No se encontraron clientes' : 'No hay clientes registrados'}
              </Typography>
            </Paper>
          ) : (
            (clientsData as any).clients?.map((client: any) => (
              <ClientCard key={client._id} client={client} />
            ))
          )}
          {(clientsData as any)?.clients && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={(clientsData as any).total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Por página:"
              sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.875rem' } }}
            />
          )}
        </Box>
      ) : (
        // Vista desktop con tabla
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Contacto</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Fecha Registro</TableCell>
                  <TableCell width={60}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="error">
                        Error: {error}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : !(clientsData as any)?.clients || (clientsData as any).clients?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        {searchTerm ? 'No se encontraron clientes' : 'No hay clientes registrados'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (clientsData as any).clients?.map((client: any) => (
                    <ClientRow
                      key={client._id}
                      client={client}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDeactivate={handleDeactivate}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {(clientsData as any)?.clients && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={(clientsData as any).total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
            />
          )}
        </Paper>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            disabled={deactivating}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmDialog.action}
            color="error"
            disabled={deactivating}
          >
            {deactivating ? <CircularProgress size={20} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add client"
        onClick={handleAddClient}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', md: 'none' },
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default ClientsPage;